import React from "react";

import { useAppSelector } from "../../../app/hooks";
import {
  OptimizedPlan,
  OptimizedRoute,
  OptimizedStop,
  planIsInfeasible,
} from "../../../models";
import { dumpsterSizeToText } from "../../../models/Order";
import { RouteMap } from "./RouteMap";

import "./OptionDetail.scss";

// Palette cycling by route index (used for the map pins/tabs, per driver).
// Saturated, mutually distinct hues that deliberately avoid the facility colors
// (dump yard #013220 dark green, storage #A89D92 tan) — no greens, no tan.
const ROUTE_COLORS = [
  "#2563EB", // blue
  "#DC2626", // red
  "#9333EA", // purple
  "#DB2777", // pink
  "#0891B2", // cyan
  "#EA580C", // orange
  "#CA8A04", // gold
  "#4F46E5", // indigo
  "#C026D3", // fuchsia
  "#64748B", // slate
];

export function routeColor(index: number): string {
  return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

// "10×3 · 15×1" from a size->count empties map (numeric size order, zeros hidden).
function formatEmpties(m?: Record<string, number>): string {
  if (!m) return "";
  const parts = Object.keys(m)
    .filter((k) => m[k] > 0)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => `${k}×${m[k]}`);
  return parts.length ? parts.join(" · ") : "none";
}

// Stop-type marker colors live in OptionDetail.scss (od-stop-marker--<type>),
// sourced from the shared $color-* Schedules variables.

export interface OptionDetailProps {
  plan: OptimizedPlan;
}

export function OptionDetail({ plan }: OptionDetailProps) {
  // Default to the "All" overview when a run opens.
  const [focusedRouteIndex, setFocusedRouteIndex] =
    React.useState<number | "all">("all");
  const isAll = focusedRouteIndex === "all";
  // In the All overview, what's highlighted (others dim): a driver index, or a
  // facility type ("dump_yard" / "storage"), or nothing.
  const [highlighted, setHighlighted] = React.useState<
    number | "dump_yard" | "storage" | null
  >(null);

  const drivers = useAppSelector((state) => state.driversV2.drivers);
  const vehicles = useAppSelector((state) => state.vehicles.vehicles);
  const allDeliveries = useAppSelector((state) => state.deliveries.deliveries);
  const allPickups = useAppSelector((state) => state.pickups.pickups);
  const allTemplates = useAppSelector((state) => state.dumpYards.templates);

  // Clamp if plan changed and focusedRouteIndex is out of range
  const safeIndex = isAll
    ? 0
    : Math.min(focusedRouteIndex as number, plan.routes.length - 1);
  const focusedRoute = plan.routes[safeIndex];

  const getDriverName = (driverId: string): string => {
    const d = drivers.find((dr) => dr.userId === driverId || dr.id === driverId);
    return d?.name ?? `Driver #${driverId}`;
  };

  const getVehicleName = (vehicleId: string): string => {
    const v = vehicles.find((ve) => ve.id === vehicleId);
    return v?.label ?? `Vehicle #${vehicleId}`;
  };

  // Business service type (dumpster / junk) for a delivery or pickup, from its
  // order record. Facilities and driver starts have none.
  const resolveServiceType = (stop: OptimizedStop): string | null => {
    if (stop.type === "delivery") {
      return allDeliveries.find((x) => x.id === stop.id)?.service ?? null;
    }
    if (stop.type === "pickup") {
      return allPickups.find((x) => x.id === stop.id)?.service ?? null;
    }
    return null;
  };

  // Dumpster size for a delivery/pickup, from its order record.
  const resolveSize = (stop: OptimizedStop): string | null => {
    const rec =
      stop.type === "delivery"
        ? allDeliveries.find((x) => x.id === stop.id)
        : stop.type === "pickup"
        ? allPickups.find((x) => x.id === stop.id)
        : undefined;
    const size = rec?.dumpsterSize;
    return size ? dumpsterSizeToText[size] ?? String(size) : null;
  };

  const resolveStopName = (stop: OptimizedStop): string => {
    try {
      if (stop.type === "delivery") {
        const d = allDeliveries.find((x) => x.id === stop.id);
        if (d) return d.address ? `${d.name} — ${d.address}` : d.name;
      } else if (stop.type === "pickup") {
        const p = allPickups.find((x) => x.id === stop.id);
        if (p) return p.address ? `${p.name} — ${p.address}` : p.name;
      } else if (stop.type === "dump_yard" || stop.type === "storage") {
        // The optimizer's dump_yard/storage id IS the dump_yard_template id
        // (the backend reads facilities straight from dump_yard_templates), so
        // resolve the name from the templates directly — not the scheduled yards.
        const tmpl = allTemplates.find((t) => t.id === stop.id);
        if (tmpl) return tmpl.address ? `${tmpl.name} — ${tmpl.address}` : tmpl.name;
      } else if (stop.type === "driver_start") {
        const driver = drivers.find(
          (dr) => dr.userId === stop.id || dr.id === stop.id
        );
        if (driver) return driver.address ? `${driver.name} — ${driver.address}` : driver.name;
      }
    } catch (_) {
      // defensive fallthrough
    }
    return `${stop.type} #${stop.id}`;
  };

  // Storage-yard name from a dump_yard_template id (used by the infeasibility panel).
  const yardName = (id: string): string =>
    allTemplates.find((t) => t.id === id)?.name ?? `Yard #${id}`;

  const routeTotals = (route: OptimizedRoute) => ({
    cost: route.cost.toFixed(2),
    fuel: route.fuelCost.toFixed(2),
    wage: route.wageCost.toFixed(2),
    dump: route.dumpFees.toFixed(2),
    maintenance: (route.maintenanceCost ?? 0).toFixed(2),
    miles: (route.meters / 1609.34).toFixed(1),
    driveHrs: (route.drivingSeconds / 3600).toFixed(1),
    serviceHrs: (route.serviceSeconds / 3600).toFixed(1),
    totalHrs: ((route.drivingSeconds + route.serviceSeconds) / 3600).toFixed(1),
  });

  const totals = routeTotals(focusedRoute);
  const driverName = getDriverName(focusedRoute.driverId);
  const vehicleName = getVehicleName(focusedRoute.vehicleId);

  return (
    <div className="option-detail" data-testid="option-detail">
      {/* Driver tabs — "All" overview first, then one per driver */}
      <div className="od-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={isAll}
          className={`od-tab ${isAll ? "od-tab--active" : ""}`}
          onClick={() => setFocusedRouteIndex("all")}
        >
          All · {plan.routes.length}
        </button>
        {plan.routes.map((route, i) => {
          const name = getDriverName(route.driverId);
          const stopCount = route.stops.filter((s) => s.type !== "driver_start").length;
          const color = routeColor(i);
          const active = !isAll && i === safeIndex;
          return (
            <button
              key={i}
              role="tab"
              aria-selected={active}
              className={`od-tab ${active ? "od-tab--active" : ""}`}
              style={active ? { borderBottomColor: color, color } : undefined}
              onClick={() => setFocusedRouteIndex(i)}
            >
              {name} · {stopCount}
            </button>
          );
        })}
      </div>

      {/* Focused route content */}
      <div className="od-body">
        {/* Option-level infeasibility banner — shown on every tab (incl. All) so the
            reason is visible without hunting for the stuck driver's route. */}
        {planIsInfeasible(plan) && (
          <div className="od-infeasible-banner" role="alert">
            <div className="od-infeasible-banner-title">
              No feasible plan — the optimizer couldn't route every job
            </div>
            <ul className="od-infeasible-banner-list">
              {plan.routes
                .filter((r) => !r.feasible)
                .map((r, i) => (
                  <li key={i}>
                    <b>{getDriverName(r.driverId)}</b>
                    {r.infeasibility ? `: ${r.infeasibility.detail}` : ""}
                  </li>
                ))}
            </ul>
          </div>
        )}
        {/* Route header (hidden in the All overview) */}
        {!isAll && (
        <>
        <div className="od-route-header">
          <div className="od-route-names">
            <span className="od-driver-name" style={{ color: routeColor(safeIndex) }}>
              {driverName}
            </span>
            <span className="od-vehicle-name">{vehicleName}</span>
          </div>
          <span
            className={`od-feasible-badge ${
              focusedRoute.feasible ? "od-feasible-badge--ok" : "od-feasible-badge--warn"
            }`}
          >
            {focusedRoute.feasible ? "Feasible" : "Infeasible"}
          </span>
          <div className="od-route-totals">
            <span className="od-total-item">
              <span className="od-total-label">Cost</span>
              <span className="od-total-value">
                {focusedRoute.feasible ? `$${totals.cost}` : "—"}
              </span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Fuel</span>
              <span className="od-total-value">${totals.fuel}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Wage</span>
              <span className="od-total-value">${totals.wage}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Dump</span>
              <span className="od-total-value">${totals.dump}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Maint</span>
              <span className="od-total-value">${totals.maintenance}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Miles</span>
              <span className="od-total-value">{totals.miles}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Drive hrs</span>
              <span className="od-total-value">{totals.driveHrs}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Service hrs</span>
              <span className="od-total-value">{totals.serviceHrs}</span>
            </span>
            <span className="od-total-item">
              <span className="od-total-label">Total hrs</span>
              <span className="od-total-value">{totals.totalHrs}</span>
            </span>
          </div>
        </div>
        {focusedRoute.penalties && focusedRoute.penalties.length > 0 && (
          <div className="od-penalties">
            <span className="od-penalties-label">Penalties</span>
            {focusedRoute.penalties.map((p, i) => (
              <span key={i} className="od-penalty">
                {p.type.replace(/_/g, " ")} ${p.amount.toFixed(2)}
              </span>
            ))}
          </div>
        )}
        {!focusedRoute.feasible && focusedRoute.infeasibility && (
          <div className="od-infeasibility">
            <div className="od-infeasibility-head">
              <span className="od-infeasibility-title">Why infeasible</span>
              <span className="od-infeasibility-detail">
                {focusedRoute.infeasibility.detail}
              </span>
            </div>
            {(focusedRoute.infeasibility.neededSize ||
              focusedRoute.infeasibility.wasteType) && (
              <div className="od-infeasibility-need">
                needed{" "}
                {focusedRoute.infeasibility.neededSize && (
                  <b>{focusedRoute.infeasibility.neededSize}</b>
                )}
                {focusedRoute.infeasibility.wasteType &&
                  ` · ${focusedRoute.infeasibility.wasteType}`}{" "}
                at {resolveStopName(
                  { type: focusedRoute.infeasibility.stopType,
                    id: focusedRoute.infeasibility.stopId } as OptimizedStop
                )}
              </div>
            )}
            {focusedRoute.infeasibility.storages.length > 0 && (
              <div className="od-infeasibility-storages">
                <span className="od-infeasibility-storages-label">
                  Storage at failure
                </span>
                {focusedRoute.infeasibility.storages.map((s, i) => (
                  <span key={i} className="od-infeasibility-yard">
                    {yardName(s.id)}: {formatEmpties(s.counts)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}

        <div className="od-content">
          {isAll ? (
            /* All-drivers legend: color, name, stop count, cost, feasibility */
            <div className="od-legend">
              {plan.routes.map((route, i) => {
                const stopCount = route.stops.filter(
                  (s) => s.type !== "driver_start"
                ).length;
                const on = highlighted === i;
                return (
                  <button
                    key={i}
                    type="button"
                    aria-pressed={on}
                    className={`od-legend-row ${on ? "od-legend-row--on" : ""}`}
                    onClick={() => setHighlighted((h) => (h === i ? null : i))}
                  >
                    <span
                      className="od-legend-swatch"
                      style={{ background: routeColor(i) }}
                    />
                    <span className="od-legend-name">
                      {getDriverName(route.driverId)}
                    </span>
                    <span className="od-legend-meta">
                      {stopCount} stops
                      {route.feasible && ` · $${route.cost.toFixed(2)}`}
                      {route.feasible &&
                        route.penalties &&
                        route.penalties.length > 0 &&
                        ` · pen $${route.penalties
                          .reduce((s, p) => s + p.amount, 0)
                          .toFixed(2)}`}
                      {!route.feasible && " · ⚠ infeasible"}
                    </span>
                  </button>
                );
              })}
              {/* Shared facilities — click to highlight them on the map */}
              <div className="od-legend-facilities">
                <button
                  type="button"
                  aria-pressed={highlighted === "dump_yard"}
                  className={`od-legend-facility ${
                    highlighted === "dump_yard" ? "od-legend-facility--on" : ""
                  }`}
                  onClick={() =>
                    setHighlighted((h) => (h === "dump_yard" ? null : "dump_yard"))
                  }
                >
                  <span
                    className="od-legend-swatch"
                    style={{ background: "#013220" }}
                  />
                  Dump yard
                </button>
                <button
                  type="button"
                  aria-pressed={highlighted === "storage"}
                  className={`od-legend-facility ${
                    highlighted === "storage" ? "od-legend-facility--on" : ""
                  }`}
                  onClick={() =>
                    setHighlighted((h) => (h === "storage" ? null : "storage"))
                  }
                >
                  <span
                    className="od-legend-swatch"
                    style={{ background: "#A89D92" }}
                  />
                  Storage
                </button>
                <span className="od-legend-facility od-legend-facility--static">
                  <span className="od-legend-start-glyph">S</span>
                  Driver start
                </span>
              </div>
            </div>
          ) : (
          /* Itinerary */
          <div className="od-itinerary">
            {focusedRoute.stops.map((stop, si) => {
              const isStart = stop.type === "driver_start";
              // Sequence label: driver_start = "S", others = 1-based index among non-start stops
              const seqNum = isStart
                ? null
                : focusedRoute.stops
                    .slice(0, si + 1)
                    .filter((s) => s.type !== "driver_start").length;
              const label = isStart ? "S" : String(seqNum);
              const showLeg = !isStart && stop.legMeters > 0;
              const legMi = (stop.legMeters / 1609.34).toFixed(1);
              const legMin = Math.round(stop.legSeconds / 60);
              const name = resolveStopName(stop);
              const typeLabel = stop.type.replace(/_/g, " ");
              const serviceType = resolveServiceType(stop);
              const size = resolveSize(stop);

              return (
                <div key={si} className="od-stop">
                  {showLeg && (
                    <div className="od-leg">
                      <span className="od-leg-line" style={{ borderColor: "#D0D0D0" }} />
                      <span className="od-leg-text">{legMi} mi · {legMin} min</span>
                    </div>
                  )}
                  <div className="od-stop-row">
                    <div className={`od-stop-marker od-stop-marker--${stop.type}`}>
                      {label}
                    </div>
                    <div className="od-stop-info">
                      <div className="od-stop-badges">
                        <span
                          className={`od-stop-type-badge od-stop-type-badge--${stop.type}`}
                        >
                          {typeLabel}
                        </span>
                        {serviceType && (
                          <span
                            className={`od-stop-service-badge od-stop-service-badge--${serviceType}`}
                          >
                            {serviceType}
                          </span>
                        )}
                        {size && <span className="od-stop-size-badge">{size}</span>}
                      </div>
                      <span className="od-stop-name">{name}</span>
                      <span className="od-stop-eta">ETA {stop.eta}</span>
                      {stop.note && (
                        <span className="od-stop-note">{stop.note}</span>
                      )}
                      {stop.dumpFee != null && (
                        <span className="od-stop-fee">
                          Dump fee ${stop.dumpFee.toFixed(2)}
                        </span>
                      )}
                      {(stop.inventoryBefore || stop.inventoryAfter) && (
                        <span className="od-stop-inventory">
                          empties {formatEmpties(stop.inventoryBefore)} →{" "}
                          {formatEmpties(stop.inventoryAfter)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {/* Map */}
          <div className="od-map-container">
            <RouteMap
              plan={plan}
              focusedRouteIndex={isAll ? "all" : safeIndex}
              highlightedRouteIndex={
                isAll && typeof highlighted === "number" ? highlighted : null
              }
              highlightedFacility={
                isAll && (highlighted === "dump_yard" || highlighted === "storage")
                  ? highlighted
                  : null
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
